import type { CSSProperties, FC } from "react";
import { useEffect } from "react";

import Container from "@/design-system/container";
import { useViewportInfoContext } from "@/src/contexts";
import DiscountsPanel, { type IDiscountsPanel } from "@/src/discount/DiscountsPanel/DiscountsPanel";
import type { IAjax } from "@Components/Hooks/useAjax";
import ImageLoader, { type IImageLoader } from "@Components/ImageLoader";
import { Link, LinkTarget } from "@Components/Link";
import { requestTrack } from "@Components/Modules/Helpers";

import ContentContainer, { type IContentContainer } from "../common/ContentContainer";

import * as cssPortal from "../shared/Portal.module.css";
import * as css from "./StampLoyaltyCard.module.css";

export interface IStampLoyaltyCard extends IAjax<void> {
    discountsPanel?: IDiscountsPanel;
    prizeBox: ITitleDescriptionsTuple;
    descriptionBoxes: ITitleDescriptionTuple[];
    productsDescription: IProductsDescriptionTuple[];
    titleDescription: ITitleDescriptionTuple;
    linksTitleDescription: ITitleDescriptionTuple;
    footerDescription: string;
    linkGoogleStore?: string;
    linkAppleStore?: string;
    phoneLoyaltyCardImage?: string;
    productBannerImage?: string;
    footerImage?: string;
    headerColor?: string;
    headerBackgroundColor?: string;
    textColor?: string;
    textContentColor?: string;
    secondTextColor?: string;
    imageGoogleStore: IImageLoader;
    imageAppleStore: IImageLoader;
    iconsList: string[];
    contentContainers?: IContentContainer;
}

export enum StampsType {
    Desert,
    Milkshake,
    Coffee,
}

interface ITitleDescriptionsTuple {
    item1: string;
    item2: string[];
}

interface ITitleDescriptionTuple {
    item1: string;
    item2: string;
}

interface IProductsDescriptionTuple {
    item1: string;
    item2: string;
    item3: StampsType;
}

const StampLoyaltyCard: FC<IStampLoyaltyCard> = (props) => {
    const { isMobile } = useViewportInfoContext();
    useEffect(() => {
        requestTrack(props.ajaxAction);
    }, []);

    const showButtons: boolean = !!(props.linkGoogleStore && props.linkAppleStore);

    let titleContainerClass = [css.titleContainer];
    let productsGroupClass = [css.productsGroup];

    if (showButtons === true) {
        titleContainerClass = [...titleContainerClass, css.withButtons];
        productsGroupClass = [...productsGroupClass, css.withButtons];
    }

    const mapDescriptionBoxes = (value: ITitleDescriptionTuple, index: number): React.ReactNode => (
        <div className={css.textTile} key={index}>
            <div className={css.header}>
                {props.iconsList && <img alt="ico-simple" src={props.iconsList[index]} />}
                <h4 style={{ color: props.secondTextColor }}>{value.item1}</h4>
            </div>
            <p style={{ color: props.textContentColor }} dangerouslySetInnerHTML={{ __html: value.item2 }} />
        </div>
    );

    const mapProductsDescription = (value: IProductsDescriptionTuple, index: number): React.ReactNode => {
        let className = css.cupLabel;

        switch (value.item3) {
            case StampsType.Desert:
                className = `${className} ${css.desert}`;
                break;
            case StampsType.Milkshake:
                className = `${className} ${css.milkShake}`;
                break;
            case StampsType.Coffee:
                className = `${className} ${css.coffee}`;
                break;
            default:
                break;
        }

        const style: CSSProperties = { color: props.textColor };

        return (
            <div
                style={{
                    borderColor: props.headerColor,
                    color: "#FFFFFF",
                }}
                className={className}
                key={index}
            >
                <h4 style={style} className={css.title}>
                    {value.item1}
                </h4>
                {value.item2 && (
                    <p style={style} className={css.description}>
                        {value.item2}
                    </p>
                )}
            </div>
        );
    };

    return (
        <div className={css.stampLoyaltyCardComponent}>
            <div
                style={{
                    color: props.headerColor,
                    backgroundColor: props.headerBackgroundColor,
                }}
                className={css.header}
            >
                <div className={css.content}>
                    <div className={css.top}>
                        <div className={titleContainerClass.join(" ")}>
                            <h2 className={css.title}>{props.titleDescription.item1}</h2>
                            <p style={{ color: props.textColor }} className={css.description}>
                                {props.titleDescription.item2}
                            </p>
                        </div>
                    </div>
                    <div className={productsGroupClass.join(" ")}>
                        <div className={css.wrapper}>
                            <img
                                alt="productBannerImage"
                                className={css.productsImage}
                                src={props.productBannerImage}
                            />
                            <div className={css.labelsContainer}>
                                {props.productsDescription &&
                                    props.productsDescription.map(mapProductsDescription)}
                            </div>
                        </div>
                    </div>
                </div>
                {showButtons && (
                    <div className={css.content}>
                        <div className={css.bottom}>
                            {!isMobile && (
                                <img
                                    alt="mobileIcon"
                                    className={css.mobileIcon}
                                    src={props.phoneLoyaltyCardImage}
                                />
                            )}
                            <div className={css.infoContainer}>
                                <h3
                                    style={{ color: props.textColor }}
                                    className={css.description}
                                    dangerouslySetInnerHTML={{
                                        __html: props.linksTitleDescription.item1,
                                    }}
                                />
                                <div className={css.linkContainer}>
                                    <div
                                        style={{ borderColor: props.headerColor }}
                                        className={css.linkActions}
                                    >
                                        <Link
                                            className={css.linkComponent}
                                            navigateUrl={props.linkGoogleStore}
                                            target={LinkTarget.Blank}
                                            hideMainLoading={true}
                                        >
                                            <ImageLoader
                                                style={{
                                                    borderColor: props.headerColor,
                                                }}
                                                className={css.appsStoreImage}
                                                {...props.imageGoogleStore}
                                            />
                                        </Link>
                                        <Link
                                            className={css.linkComponent}
                                            navigateUrl={props.linkAppleStore}
                                            target={LinkTarget.Blank}
                                            hideMainLoading={true}
                                        >
                                            <ImageLoader
                                                style={{
                                                    borderColor: props.headerColor,
                                                }}
                                                className={css.appsStoreImage}
                                                {...props.imageAppleStore}
                                            />
                                        </Link>
                                    </div>
                                    {isMobile && (
                                        <img
                                            alt="mobileIcon"
                                            className={css.mobileIcon}
                                            src={props.phoneLoyaltyCardImage}
                                        />
                                    )}
                                    <p style={{ color: props.textColor }} className={css.linkDescription}>
                                        {props.linksTitleDescription.item2}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div className={css.wrapper}>
                <div className={css.content}>
                    {props.descriptionBoxes.map(mapDescriptionBoxes)}
                    <div className={css.prizeBox}>
                        <h4 style={{ color: props.textContentColor }}>{props.prizeBox.item1}</h4>
                        {props.prizeBox.item2.map((value, index) => (
                            <p
                                style={{ color: props.textContentColor }}
                                className={props.prizeBox.item2.length - 1 === index ? css.last : ""}
                                key={index}
                                dangerouslySetInnerHTML={{ __html: value }}
                            />
                        ))}
                    </div>
                </div>
            </div>
            <div className={css.footer}>
                <div className={css.description}>
                    <h4
                        style={{ color: props.secondTextColor }}
                        dangerouslySetInnerHTML={{ __html: props.footerDescription }}
                    />
                </div>
                <span
                    style={{ backgroundImage: `url("${props.footerImage}")` }}
                    className={css.footerImage}
                />
            </div>
            <div className={cssPortal.standardContainer}>
                {props.discountsPanel && (
                    <DiscountsPanel {...props.discountsPanel} hideIfEmpty={true} className={css.discounts} />
                )}
            </div>

            <Container>
                <ContentContainer {...props.contentContainers} />
            </Container>
        </div>
    );
};

export { StampLoyaltyCard };
